import { useContext, useRef } from 'react';
import { isNil } from '~/utilities/type-guards';
import { trackStructEvent } from '~/utilities/snowplow';
import type { Trackable } from '~/onboarding/types';
import type { SnowplowContextType } from './SnowplowContext';
import { DEFAULT_CONTEXT_VALUE, SnowplowContext } from './SnowplowContext';
import { Events } from './Events';

type LogEventFn = (type: keyof typeof Events, overrides?: Trackable['snowplowParams']) => void;

export const useSnowplow = (): SnowplowContextType & {
  logEvent: LogEventFn;
  logEventOnce: LogEventFn;
} => {
  const context = useContext(SnowplowContext);
  const eventLog = useRef<(keyof typeof Events)[]>([]);

  const logEvent: LogEventFn = (type, overrides?) => {
    trackStructEvent({
      ...Events[type],
      label: context.label,
      value: context.step,
      ...overrides,
    });
    eventLog.current.push(type);
  };

  const logEventOnce: LogEventFn = (type, overrides?) => {
    if (!eventLog.current.includes(type)) {
      logEvent(type, overrides);
    }
  };

  if (isNil(context)) {
    return { ...DEFAULT_CONTEXT_VALUE, logEvent, logEventOnce };
  }

  return { ...context, logEvent, logEventOnce };
};
